import React, {useEffect, useMemo} from 'react';
import {NavLink} from 'react-router-dom';
import cn from 'classnames';
import {useDispatch, useSelector} from 'react-redux';
import {FormattedMessage} from 'react-intl';

import {IStore} from 'redux/interface';
import {loadTranslation} from 'redux/translation/translationAsyncActions';

import {RoutesUrl, routeTranslationKeys} from 'utils/constants/routeConstants';

import {IContentNavItem, INavItem} from '../interfaces';
import {navItems} from '../constants';

import '../styles.scss';
import {IUserProfile} from '@wholesalechange/chatcomponent';
import {
  backpackAsyncActions,
  getFavoriteNewItems,
  getNotificationCount,
  notificationAsyncActions
} from '../../../redux/pageAsyncActions';

interface INavigationProps {
  className?: string;
  onOpenMenu: () => void;
  roles: string[]
}

export const Navigation = ({
  className,
  onOpenMenu,
  roles
}: INavigationProps): JSX.Element => {
  const {
    translationKeys
  } = useSelector((state: IStore) => state.translation);
  const {
    settings, userProfile
  } = useSelector((state: IStore) => state.auth);
  const {
    favoriteNewItems
  } = useSelector((state: IStore) => state.content);

  const dispatch = useDispatch();

  const isUserProfileFilled = () => {
    if (userProfile && settings && settings.profileSettings
      && settings.profileSettings.blockUntilRequiredFieldsSet && settings.profileSettings.additionalFields) {
      const requiredFields = settings.profileSettings.additionalFields
        .filter(field => field.required)
        .map(field => field.name);

      return !requiredFields.some(field => !userProfile[field as keyof IUserProfile]);
    }

    return true;
  };

  const allowedNavItems = useMemo<INavItem[]>(() => {
    if (!settings?.pages) {
      return [];
    }

    return settings.pages.filter(page => !page.showInUserMenu).flatMap((page) => {
      const navItem = navItems.find(
        (item) => item.name.toLowerCase() === page.name.toLowerCase()
      );

      if (!navItem) {
        return [];
      }

      return {
        ...navItem,
        title: page.title || navItem.title,
        role: page.role ?? navItem.role ?? null
      };
    });
  }, [settings?.pages]);

  const allowedContentNavItems = useMemo<IContentNavItem[]>(() => {
    if (!settings?.contentPages) {
      return [];
    }

    return settings.contentPages.filter(page => !page.showInUserMenu).flatMap((page, i) => {
      return {
        index: i,
        path: `${RoutesUrl.Content}#${page.contentId}`,
        title: page.title,
        role: page.role ?? null
      };
    });
  }, [settings?.contentPages]);

  function handleMenuItemClick(route: RoutesUrl) {
    return () => {
      if (!translationKeys.includes(routeTranslationKeys[route])) {
        dispatch(loadTranslation(routeTranslationKeys[route]));
      }
      dispatch(backpackAsyncActions[getFavoriteNewItems]());
      dispatch(notificationAsyncActions[getNotificationCount]());
      onOpenMenu();
    };
  }
  function handleContentMenuItemClick() {
    if (!translationKeys.includes(routeTranslationKeys[RoutesUrl.Content])) {
      dispatch(loadTranslation(routeTranslationKeys[RoutesUrl.Content]));
    }
    onOpenMenu();
  }

  return (<nav className={cn('nav', className)}>
    {
      isUserProfileFilled() && allowedNavItems.map((item, index) => {
        if (item.role && !roles.includes(item.role)) {
          return null;
        }

        return (<div key={index} className="nav__item">
          <NavLink
            to={item.path}
            className={(nav)=>nav.isActive ? 'nav__link nav__link-active' : 'nav__link'}
            onClick={handleMenuItemClick(item.path)}
          >
            <div className="nav__item-wrapper">
              <div className="nav__img-wrapper">
                <img className="nav__img" src={item.image} alt="" role="presentation"/>
              </div>
              <p className="nav__item-text">
                <FormattedMessage
                  id={`gritx.common.navigation.${item.title}`}
                  defaultMessage={item.title}
                />
                {item.path === RoutesUrl.Backpack && !!(favoriteNewItems?.length) && (
                  <span className="nav__badge">{favoriteNewItems!.length}</span>
                )}
              </p>
            </div>
          </NavLink>
        </div>);
      })
    }
    {
      isUserProfileFilled() && allowedContentNavItems.map((item, index) => {
        if (item.role && !roles.includes(item.role)) {
          return null;
        }

        return (<div key={index} className="nav__item">
          <NavLink
            to={item.path}
            className={(nav)=>nav.isActive ? 'nav__link nav__link-active' : 'nav__link'}
            onClick={handleContentMenuItemClick}
          >
            <div className="nav__item-wrapper">
              <div className="nav__img-wrapper">
              </div>
              <p className="nav__item-text">
                <FormattedMessage
                  id={`gritx.common.navigation.${item.title}`}
                  defaultMessage={item.title}
                />
              </p>
            </div>
          </NavLink>
        </div>);
      })
    }
  </nav>);
};
